import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  // Dynamic API base URL (matches production)
  const API_BASE_URL = window._env_?.REACT_APP_API_BASE_URL || 'https://installmentzone.com/api';

  // === Login Handler ===
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin_panel/v1/auth/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Invalid credentials');

      if (data.access_token && data.role) {
        login(data.access_token, data.role, data.permissions || []);

        const userRole = data.role.toLowerCase();
        let redirectPath = '/dashboard';

        switch (userRole) {
          case 'super_admin':
            redirectPath = '/super-admin-dashboard';
            break;
          case 'administrator':
          case 'admin':
          case 'manager':
          case 'agent':
            redirectPath = '/dashboard';
            break;
          default:
            redirectPath = '/dashboard';
        }

        setSuccessMessage(`Login successful as ${data.role}. Redirecting...`);
        setTimeout(() => navigate(redirectPath, { replace: true }), 1000);
      } else {
        setError('Login failed: access token or role missing.');
      }
    } catch (err) {
      setError(err.message || 'Error logging in. Please try again.');
    }
  };

  // === OTP Request ===
  const handleOTPRequest = async () => {
    setError('');
    setSuccessMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin_panel/v1/auth/request-otp.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send OTP');
      setOtpSent(true);
      setSuccessMessage('OTP sent to your email.');
    } catch (err) {
      setError(err.message || 'Error requesting OTP.');
    }
  };

  // === OTP Verification ===
  const handleOTPVerification = async () => {
    setError('');
    setSuccessMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin_panel/v1/auth/verify-otp.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Invalid OTP');
      setOtpVerified(true);
      setSuccessMessage('OTP verified. Create your password.');
    } catch (err) {
      setError(err.message || 'Error verifying OTP.');
    }
  };

  // === Create Password ===
  const handlePasswordCreation = async () => {
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setSuccessMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin_panel/v1/auth/create-password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to create password');
      setSuccessMessage('Password created. Redirecting to login...');
      setTimeout(() => toggleForm(), 2000);
    } catch (err) {
      setError(err.message || 'Error creating password.');
    }
  };

  // === Toggle Form Mode ===
  const toggleForm = () => {
    setIsNewUser(!isNewUser);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setOtp('');
    setOtpSent(false);
    setOtpVerified(false);
    setError('');
    setSuccessMessage('');
  };

  // === Unified Submit Handler ===
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isNewUser) {
      if (otpSent) {
        if (otpVerified) {
          handlePasswordCreation();
        } else {
          handleOTPVerification();
        }
      } else {
        handleOTPRequest();
      }
    } else {
      handleLogin(e);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(-45deg, #1e3a8a, #3b82f6, #06b6d4, #10b981)',
        backgroundSize: '400% 400%',
        animation: 'gradient 15s ease infinite',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        fontFamily: '"Segoe UI", Roboto, sans-serif',
      }}
    >
      <style>
        {`
          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .glass-card {
            animation: fadeIn 0.6s ease-out;
          }
          input::placeholder {
            color: rgba(255, 255, 255, 0.6);
          }
          input:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
            background: rgba(255, 255, 255, 0.25);
          }
        `}
      </style>

      {/* Glassmorphic Card */}
      <div
        className="glass-card"
        style={{
          width: '100%',
          maxWidth: '420px',
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '20px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '2.5rem',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s ease',
        }}
      >
        {/* Logo / Brand */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
              borderRadius: '50%',
              margin: '0 auto 1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '2rem',
              fontWeight: 'bold',
              boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
            }}
          >
            IZ
          </div>
          <h1
            style={{
              fontSize: '1.8rem',
              fontWeight: '700',
              color: 'white',
              margin: '0 0 0.5rem 0',
              letterSpacing: '-0.5px',
            }}
          >
            InstallmentZone
          </h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: 0, fontSize: '0.95rem' }}>
            {isNewUser ? 'Activate Your Account' : 'Admin Panel Login'}
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div
            style={{
              background: 'rgba(239, 68, 68, 0.2)',
              color: '#fca5a5',
              padding: '0.75rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              textAlign: 'center',
              fontSize: '0.9rem',
              border: '1px solid rgba(239, 68, 68, 0.3)',
            }}
          >
            {error}
          </div>
        )}
        {successMessage && (
          <div
            style={{
              background: 'rgba(34, 197, 94, 0.2)',
              color: '#86efac',
              padding: '0.75rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              textAlign: 'center',
              fontSize: '0.9rem',
              border: '1px solid rgba(34, 197, 94, 0.3)',
            }}
          >
            {successMessage}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="email"
              style={{
                display: 'block',
                color: 'white',
                fontWeight: '500',
                marginBottom: '0.5rem',
                fontSize: '0.95rem',
              }}
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '10px',
                color: 'white',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
              }}
              placeholder="Enter your email"
            />
          </div>

          {/* Password (Login or Set New) */}
          {(!isNewUser || otpVerified) && (
            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="password"
                style={{
                  display: 'block',
                  color: 'white',
                  fontWeight: '500',
                  marginBottom: '0.5rem',
                  fontSize: '0.95rem',
                }}
              >
                {otpVerified ? 'New Password' : 'Password'}
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  color: 'white',
                  fontSize: '1rem',
                }}
                placeholder={otpVerified ? 'Create strong password' : 'Enter password'}
              />
            </div>
          )}

          {/* Confirm Password */}
          {isNewUser && otpVerified && (
            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="confirmPassword"
                style={{
                  display: 'block',
                  color: 'white',
                  fontWeight: '500',
                  marginBottom: '0.5rem',
                  fontSize: '0.95rem',
                }}
              >
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  color: 'white',
                  fontSize: '1rem',
                }}
                placeholder="Confirm your password"
              />
            </div>
          )}

          {/* OTP Field */}
          {isNewUser && otpSent && !otpVerified && (
            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="otp"
                style={{
                  display: 'block',
                  color: 'white',
                  fontWeight: '500',
                  marginBottom: '0.5rem',
                  fontSize: '0.95rem',
                }}
              >
                Enter OTP
              </label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '10px',
                  color: 'white',
                  fontSize: '1rem',
                  letterSpacing: '0.5rem',
                  textAlign: 'center',
                }}
                placeholder="------"
              />
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            style={{
              width: '100%',
              padding: '0.9rem',
              background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              marginTop: '0.5rem',
              boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
            onMouseOut={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            {isNewUser
              ? otpSent
                ? otpVerified
                  ? 'Create Password'
                  : 'Verify OTP'
                : 'Request OTP'
              : 'Login to Dashboard'}
          </button>

          {/* Toggle Link */}
          <button
            type="button"
            onClick={toggleForm}
            style={{
              marginTop: '1.5rem',
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.9)',
              cursor: 'pointer',
              textAlign: 'center',
              width: '100%',
              fontSize: '0.95rem',
              textDecoration: 'underline',
              transition: 'color 0.2s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = 'white')}
            onMouseOut={(e) => (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)')}
          >
            {isNewUser ? 'Back to Login' : 'New User? Activate Account'}
          </button>
        </form>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '2rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
          &copy; 2025 InstallmentZone. All rights reserved.
        </div>
      </div>
    </div>
  );
}

export default LoginPage;